import {
  API_HTTP_METHOD_VALUES,
  type ApiDeliveryEndpointConfig,
} from '@steerfi/shared/types/jsonb/integration-profile.types';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import type React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { KvEditor } from './kv-editor';

interface EndpointsEditorProps {
  value: Record<string, ApiDeliveryEndpointConfig>;
  onChange: (v: Record<string, ApiDeliveryEndpointConfig>) => void;
  disabled?: boolean;
}

export const EndpointsEditor: React.FC<EndpointsEditorProps> = ({ value, onChange, disabled }) => {
  const isDisabled = disabled ?? false;
  const entries = Object.entries(value);

  const addEndpoint = () => {
    let name = 'endpoint';
    let i = 1;
    while (Object.hasOwn(value, name)) name = `endpoint${i++}`;
    onChange({ ...value, [name]: { path: '' } });
  };

  const removeEndpoint = (key: string) => {
    const next = { ...value };
    delete next[key];
    onChange(next);
  };

  const renameEndpoint = (oldKey: string, newKey: string) => {
    if (newKey !== oldKey && Object.hasOwn(value, newKey)) return;
    const next: Record<string, ApiDeliveryEndpointConfig> = {};
    for (const [k, v] of Object.entries(value)) {
      next[k === oldKey ? newKey : k] = v;
    }
    onChange(next);
  };

  const updateEndpoint = (key: string, patch: Partial<ApiDeliveryEndpointConfig>) => {
    onChange({ ...value, [key]: { ...value[key], ...patch } });
  };

  const deleteEndpointField = (key: string, field: keyof ApiDeliveryEndpointConfig) => {
    const next = { ...value[key] };
    delete next[field];
    onChange({ ...value, [key]: next });
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Endpoints</Label>
      {entries.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No endpoints yet.</p>
      )}
      <div className="space-y-2">
        {entries.map(([key, ep]) => (
          <div key={key} className="rounded-lg border p-3 space-y-2">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">
                Name <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  className="h-7 text-xs font-mono flex-1"
                  disabled={isDisabled}
                  placeholder="operationName"
                  value={key}
                  onChange={(e) => renameEndpoint(key, e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                  disabled={isDisabled}
                  onClick={() => removeEndpoint(key)}
                >
                  <IconTrash size={13} />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">
                  Path <span className="text-destructive">*</span>
                </Label>
                <Input
                  className="h-7 text-xs font-mono"
                  disabled={isDisabled}
                  placeholder="/v1/operation"
                  value={ep.path}
                  onChange={(e) => updateEndpoint(key, { path: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Method</Label>
                <Select
                  disabled={isDisabled}
                  value={ep.method ?? ''}
                  onValueChange={(v) => {
                    if (v)
                      updateEndpoint(key, { method: v as (typeof API_HTTP_METHOD_VALUES)[number] });
                    else deleteEndpointField(key, 'method');
                  }}
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue placeholder="Inherit" />
                  </SelectTrigger>
                  <SelectContent>
                    {API_HTTP_METHOD_VALUES.map((m) => (
                      <SelectItem key={m} value={m} className="text-xs">
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Timeout (ms)</Label>
                <Input
                  type="number"
                  className="h-7 text-xs"
                  disabled={isDisabled}
                  placeholder="Inherit"
                  value={ep.timeoutMs ?? ''}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (e.target.value && !isNaN(n)) updateEndpoint(key, { timeoutMs: n });
                    else if (!e.target.value) deleteEndpointField(key, 'timeoutMs');
                  }}
                />
              </div>
            </div>
            <KvEditor
              label="Headers (override)"
              disabled={isDisabled}
              value={ep.headers ?? {}}
              onChange={(headers) => {
                if (Object.keys(headers).length) updateEndpoint(key, { headers });
                else deleteEndpointField(key, 'headers');
              }}
            />
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 text-xs gap-1"
        disabled={isDisabled}
        onClick={addEndpoint}
      >
        <IconPlus size={12} />
        Add endpoint
      </Button>
    </div>
  );
};
